import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/ducks';

import { Tooltip } from 'react-tooltip';
import Spinner from 'react-spinkit';

import { Row, Column } from '../Layout/Grid';
import Text from '../Text';
import {
  pedidosAction,
  protocolosAction,
  cotacoesAction
} from 'components/ApplicationInsights/eventNames';
import Loading from '../Loading';
import Paginator from '../Paginator';
import { Button } from '../Button';
import RefreshButton from 'components/RefreshButton';
import { useCustomEvent } from 'components/ApplicationInsights';
import { ActionButton } from 'components/ModelButton';
import { Visibility } from 'components/Visibility';
import toast from 'components/Toast/dispatch';
import OptionsDownloadInLot from './OptionsDownloadInLot';
import { TableItem } from './TableItem';

import iconDownload from 'img/arrow-download.svg';
import iconDownloadLote from 'img/icon-download.svg';
import iconOrderActiveDesc from 'img/icon-order-active-desc.svg';
import iconOrderActiveAsc from 'img/icon-order-active-asc.svg';
import iconOrderInactive from 'img/icon-order-inactive.svg';
import iconDownloadInLot from 'img/download-in-lot.svg';
import IconArrowUp from 'img/arrow-up.svg';
import IconDown from 'img/arrow-down.svg';

import DomainSettingService from "services/domainSetting.service";

import { ITable, ITableModel, OrderBulkDownloadProps } from './interface';
import { TransactionInput } from 'models/Order';
import { IsNullOrEmpty } from 'utils/StringNullOrEmpty';

import './styles.scss';
import { isMobileDevice } from 'utils/ScreenUtils';

/**
 * @param {Array<ITableModel>} model
 * @param {Object} modelItem {
 * @param {string} label It's a table label that renders in a column.
 * @param {string} [key] If it's provided, a Text component is rendered with this key value.
 * @param {Function} [Component] Render a custom component instead Text and it recives an object of row to use that in render
 *
 * Ex: Component: (data) => <Button>{data.any}</Button>
 * @param {number} [size] It's a minimum size of row item
 * @param {number} [maxSize] It's a maximum size of row item (if you don't pass this property, the row item turns flex)
 * @modelItem}
 *
 * @param {boolean} [completeBlankSpaces] If it's true, render more table rows to match "pageSize" number
 */

const RowSpacing = ({ height, backgroundColor }: { height: number; backgroundColor: string; }) => {
  return (
    <Row
      className="spacing-row"
      style={{
        height: height,
        backgroundColor: backgroundColor,
      }}
    />
  );
};

const Table: React.FC<ITable> = (props) => {

  const {
    className,
    pageSize,
    showPagination,
    data,
    model,
    currentPage,
    completeBlankSpaces,
    bottomButton,
    totalPages,
    DropdownTemplate,
    error,
    onReload,
    setTableOrder,
    tableOrder,
    onPageChange,
    customProps,
    rowHeight,
    redirectUrl,
    trackingKey,
    exportButton,
    isRanking,
    downloadInLotButton,
    getOrdersForDownloadInLot,
    ordersDownloadInLot,
    totalOrder,
    sentMoney,
    portalCustomHeader,
    portalCustomRow,
    isCustomTableColor,
    emptyDataMessage = ""
  } = props;

  const isEven = (el: number) => (el % 2 !== 0 ? 'bg-white' : 'bg-gray-10');
  const isEvenSelectedOrder = (el: number) => (el % 2 !== 0 ? 'bg-orange-light' : 'bg-orange-dark');

  const dispatch = useDispatch();
  const trackOrderBy = useCustomEvent(trackingKey ?? '', 'Ordenar');
  const user = useSelector((state: RootState) => state.user);

  const [hideShowTooltip, setHideShowTooltip] = useState(false);
  const [showOptionsDownloadInLot, setShowOptionsDownloadInLot] = useState<boolean>(false);
  const [selectedOrderDownloadInLot, setSelectedOrderDownloadInLot] = useState<TransactionInput[]>([]);

  const [updateTable, setUpdateTable] = useState(false);
  const [orderBulkDownload, setOrderBulkDownload] = useState<OrderBulkDownloadProps>();

  const [expandedDropdown, setExpandedDropdown] = useState<number | undefined>(undefined);

  const _domainSettingService = new DomainSettingService();

  const orderToOData = (str?: string) =>
    str?.split(' ')[0].replace('.', '/') || str;
  const isOrderAsc = (str: string) => str.split(' ')[1] === 'asc';
  const isOrderDesc = (str: string) => str.split(' ')[1] === 'desc';
  const orderToAsc = (str?: string) => `${str} asc` || str;
  const orderToDesc = (str?: string) => `${str} desc` || str;

  const wrapperRef = useRef(null);
  useMouseMove(wrapperRef);

  const componentName = 'Table';
  const trackMenuClick = useCustomEvent(componentName, 'Protocolos');
  const navigate = useNavigate();
  const labelHeight = isCustomTableColor ? 32 : rowHeight || 50;
  const isMobile = isMobileDevice();
  const backgroundClass = isMobile ? '' : 'bg-gray-20';
  const spacePaddingClass = isMobile ? 'pl-2 pt-2 pb-2' : 'p-2';
  const spaceMinWidth = isMobile ? 40 : 50;
  const valueMinHeightBottomBtn = bottomButton ? 52.8 : 0;
  const classBorderDownloadInLote = !exportButton?.show ? "border-show" : "border-hide";

  useEffect(() => {
    setExpandedDropdown(undefined);
  }, [data, showOptionsDownloadInLot]);

  useEffect(() => {
    OrderBulkDownload();
  }, []);

  useEffect(() => {
    if (showOptionsDownloadInLot === false) {
      setSelectedOrderDownloadInLot([]);
      dispatch({
        type: 'SET_VIEW_OPTIONS_DOWNLOAD_IN_LOT',
        value: false,
      });
    } else if (showOptionsDownloadInLot === true) {
      dispatch({
        type: 'SET_VIEW_OPTIONS_DOWNLOAD_IN_LOT',
        value: true,
      });
    }
  }, [showOptionsDownloadInLot]);

  useEffect(() => {
    setShowOptionsDownloadInLot(false);
  }, [user?.selectedCustomerId]);

  useEffect(() => {
    if (totalOrder && totalOrder < selectedOrderDownloadInLot.length) {
      getOrdersForDownloadInLot();
    }
  }, [totalOrder]);

  useEffect(() => {
    if (!ordersDownloadInLot?.data) return;
    const ordersDownloadInLotData: any = ordersDownloadInLot?.data;
    setSelectedOrderDownloadInLot(ordersDownloadInLotData);

  }, [ordersDownloadInLot]);

  async function OrderBulkDownload() {
    if (downloadInLotButton) {
      const result = await _domainSettingService
        .get(undefined, undefined, undefined, {
          and: [
            { identifier: { eq: "OrderBulkDownload" } },
            { enabled: { eq: true } },
          ],
        });

      if (result && result.data && result.data.length > 0) {
        const json = JSON.parse(result.data[0].value);
        setOrderBulkDownload(json);
      }
    }
  }

  function useMouseMove(ref: any) {
    useEffect(() => {
      let hideShowTooltipInside = false;
      function handleMoveMouse(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setHideShowTooltip(true);
          hideShowTooltipInside = true;
        }
      }

      !hideShowTooltipInside && document.addEventListener("mouseover", handleMoveMouse);
      return () => {
        !hideShowTooltipInside && document.removeEventListener("mouseover", handleMoveMouse);
      };
    }, [ref]);
  }

  function transformUrl(url: string, dataParam: any): string {
    const regex = /\{(.*?)\}/gm;

    return url.replace(regex, (matched) => {
      const property = matched.replace('{', '').replace('}', '');
      return dataParam[property];
    });
  }

  function getTrackName() {
    if (window.location.pathname === '/cotacoes') {
      return cotacoesAction.VISUALIZARCOTACAO;
    } else if (window.location.pathname === '/pedidos') {
      return pedidosAction.VISUALIZARPEDIDO;
    } else {
      return pedidosAction.DASHBOARD_VISUALIZARPEDIDO;
    }
  }

  function addOrRemoveOrderSelected(index: number) {
    if (data) {
      const checkIfOrderExists = selectedOrderDownloadInLot.find(x => x.id === data[index].id);

      if (checkIfOrderExists) {
        const newArrayOrderSelected = selectedOrderDownloadInLot.filter(x => x.id !== checkIfOrderExists.id);
        setSelectedOrderDownloadInLot(newArrayOrderSelected);
      } else {
        setSelectedOrderDownloadInLot([...selectedOrderDownloadInLot, data[index]]);
      }

      setUpdateTable(!updateTable);
    }
  }

  const colorRowTable = useCallback((index: number) => {
    if (data?.[index]?.ticketReason === "FALTA DE VOLUME")
      return "bg-alert-reason";

    if (checkIfOrderIsSelected(index)) {
      return isEvenSelectedOrder(index);
    } else {
      return isEven(index);
    }
  }, [updateTable, data, selectedOrderDownloadInLot]);

  function checkIfOrderIsSelected(index: number) {
    if (data) {

      const findOrder = selectedOrderDownloadInLot.find(x => x.id === data[index].id);
      return findOrder ? true : false;
    }

    return false;
  }

  async function downloadLoteInfo() {
    const firstTimeClickDownloadInLot = localStorage.getItem('firstTimeClickDownloadInLot');
    if (!firstTimeClickDownloadInLot) {
      localStorage.setItem('firstTimeClickDownloadInLot', 'false');

      if (orderBulkDownload) {
        toast.info(
          orderBulkDownload.titleFirstClick,
          orderBulkDownload.messageFirstClick,
          iconDownloadLote
        );
      }
    }

    setShowOptionsDownloadInLot(true);
  }

  const handleOrdersForDownloadInLot = () => {

    if (checkIfAllOrderIsSelected()) {
      setSelectedOrderDownloadInLot([]);
      return;
    }

    if (totalOrderLessThanPageSize()) return;
    if (ordersDownloadInLotNotIsNull()) return;

    getOrdersForDownloadInLot();
    setUpdateTable(!updateTable);
  };

  const totalOrderLessThanPageSize = () => {
    if (data && totalOrder && pageSize && totalOrder <= pageSize) {
      setSelectedOrderDownloadInLot(data);
      return true;
    }
    return false;
  };

  const ordersDownloadInLotNotIsNull = () => {
    const ordersDownloadInLotData: any = ordersDownloadInLot?.data;
    if (ordersDownloadInLotData) {
      setSelectedOrderDownloadInLot(ordersDownloadInLotData);
      return true;
    }
    return false;
  };

  const checkIfAllOrderIsSelected = () => {
    return (selectedOrderDownloadInLot.length === ordersDownloadInLot?.data?.length ||
      selectedOrderDownloadInLot?.length === totalOrder);
  };

  function expandedDropdownAndSendDataLayer(propsParam: any) {
    if (expandedDropdown !== propsParam.index) {
      const gtmDataLayer = (window as any).dataLayer;
      gtmDataLayer.push({
        event: 'view_tracking',
        location: window?.location?.href,
        userType: user?.userType
      });
    }

    setExpandedDropdown((old) =>
      old !== propsParam.index ? propsParam.index : undefined
    );
  }

  const Dropdown = {
    Space: () => {
      if (DropdownTemplate && !showOptionsDownloadInLot) {
        return (
          <Row
            className={`align-center ${backgroundClass} ${spacePaddingClass} flex-1 h-100`}
            style={{
              minWidth: spaceMinWidth,
              maxWidth: 50,
              width: 50,
            }}
          />
        );
      }

      if (!showOptionsDownloadInLot) return null;

      if (ordersDownloadInLot?.loading) {
        return (
          <Row
            className="align-center bg-gray-20 p-2 flex-1 h-100"
            style={{
              minWidth: 50,
              maxWidth: 50,
              width: 50,
            }}
          >
            <Spinner
              fadeIn="none"
              name="circle"
              color="#0E4C54"
              style={{
                height: 20,
                width: 20,
              }}
            />
          </Row>
        );
      }

      if (!ordersDownloadInLot?.loading) {
        return (
          <Row
            className="align-center bg-gray-20 p-2 flex-1 h-100"
            style={{
              minWidth: 50,
              maxWidth: 50,
              width: 50,
            }}
          >
            <input
              type="checkbox"
              onClick={() => handleOrdersForDownloadInLot()}
              defaultChecked={checkIfAllOrderIsSelected()}
            />
          </Row>
        );
      }

      return null;
    },
    /**
     * It's a clickable arrow icon to expand dropdown component
     */
    Arrow: (propsParam: any) => {
      if (DropdownTemplate && !showOptionsDownloadInLot) {
        return (
          <Row
            className={`align-center ${spacePaddingClass} flex-1 ${!isRanking && colorRowTable(propsParam.index)}`}
            style={{
              minWidth: spaceMinWidth,
              maxWidth: 50,
              width: 50,
              height: 50,
            }}
          >
            <Button
              key={propsParam.index}
              trackName={getTrackName()}
              onClick={() => expandedDropdownAndSendDataLayer(propsParam)}
              className="no-style"
            >
              <>
                <Visibility visible={expandedDropdown === propsParam.index}>
                  <img
                    src={IconArrowUp}
                    alt=""
                    style={{
                      transition: 'all .3s ease',
                      transform: 'rotate(180deg)',
                    }}
                  />
                </Visibility>
                <Visibility visible={expandedDropdown !== propsParam.index}>
                  <img
                    src={IconDown}
                    alt=""
                    style={{
                      transition: 'all .3s ease',
                      transform: 'rotate(0deg)',
                    }}
                  />
                </Visibility>
              </>
            </Button>
          </Row >
        );
      }

      if (showOptionsDownloadInLot) {
        return (
          <Row
            className={`align-center p-2 flex-1 ${!isRanking && colorRowTable(propsParam.index)}`}
            style={{
              minWidth: 50,
              maxWidth: 50,
              width: 50,
              height: 50,
            }}
          >
            <input
              key={propsParam.index}
              type="checkbox"
              onClick={() => addOrRemoveOrderSelected(propsParam.index)}
              defaultChecked={checkIfOrderIsSelected(propsParam.index)}
              disabled={selectedOrderDownloadInLot.length === 100 && !checkIfOrderIsSelected(propsParam.index)}
            />
          </Row >
        );
      }
      return null;
    },
  };

  const handleClickTableLabel = (field: ITableModel) => {
    if (!field.noFilter) {
      if (props.trackingKey) trackOrderBy({ item: trackingKey, chave: field.key });
      setTableOrder((order: any) => sortOrderTable(field, order));
    }
  };

  const sortOrderTable = (field: ITableModel, sort: string) => {
    const isFieldKey = typeof field.key !== 'undefined';

    if (isFieldKey && orderToOData(sort) !== orderToOData(field.key))
      return orderToAsc(orderToOData(field.key));

    if (isFieldKey && orderToOData(sort) === orderToOData(field.key) &&
      isOrderAsc(sort))
      return orderToDesc(orderToOData(field.key));

    return isFieldKey && orderToOData(sort) === orderToOData(field.key) &&
      isOrderDesc(sort) ? orderToAsc(orderToOData(field.key)) : '';
  };

  const iconSortOrderTable = (field: ITableModel) => {
    const isOrderToOData = orderToOData(tableOrder) === orderToOData(field.key);

    if (isOrderToOData && isOrderAsc(tableOrder))
      return iconOrderActiveAsc;

    return isOrderToOData && isOrderDesc(tableOrder) ?
      iconOrderActiveDesc : iconOrderInactive;
  };

  /**
   * Render table label row
   */

  const textColorClass = portalCustomHeader || isCustomTableColor ? 'color-white' : 'color-primary';
  const classNameValue = `text-left ${textColorClass} font-bold text-wrap`;

  const TableLabel = useMemo(
    () =>
      model.map((m, _) => (
        <Row
          key={_}
          className={`align-center ${isCustomTableColor ? 'bg-primary' : !isRanking && backgroundClass} ${m.isButton ? 'px-1' : `py-2 px-${isMobile ? 1 : 2}`
            } flex-1`}
          style={{
            minHeight: labelHeight,
            height: labelHeight,
            maxHeight: labelHeight,
            minWidth: m.size || 100,
            maxWidth: m.maxSize || 'auto',
            backgroundColor: portalCustomHeader ? '#0E4C54' : ''
          }}
        >
          {m.label && (
            <Button
              onClick={() => handleClickTableLabel(m)}
              className={`d-flex align-center ${m.isButton ? 'px-1' : 'p-0'} flex-wrap`}
            >
              <Row>
                <Text size={12} className={classNameValue}>{m.label}</Text>

                <Visibility visible={!portalCustomHeader && !m.noFilter}>
                  <img
                    className="ml-1"
                    src={iconSortOrderTable(m)}
                    alt=""
                  />
                </Visibility>
              </Row>
            </Button>
          )}
        </Row>
      )),
    [data, model]
  );

  /**
   * Render table row
   */
  const TableRow = (propsRow: any) =>
    useMemo(
      () => (
        <Row
          className={`align-center ${propsRow.modelItem.isButton ? 'px-1' : `py-2 px-${isMobile ? 1 : 2}`
            } flex-1 h-100 ${!isRanking && colorRowTable(propsRow.objectIndex)}
            ${showOptionsDownloadInLot && propsRow.modelItem.isButton ? 'disableIcons' : ''}`}
          style={{
            minWidth: propsRow.modelItem.size || 100,
            maxWidth: propsRow.modelItem.maxSize || 'auto',
            height: rowHeight || 50,
          }}
        >
          {isRanking && propsRow.modelItem.key === 'item' &&
            <Text className="number-order-ranking">{propsRow.objectIndex + 1}
            </Text>
          }
          {propsRow.children}
        </Row>
      ),
      [propsRow]
    );

  const TableRowExtra = () => {
    const extraCount = pageSize || 20;
    const extraTableRender: any = [];
    if (data && data.length < extraCount && completeBlankSpaces) {
      for (let i = data.length; i < extraCount; i++) {
        extraTableRender.push(
          <Row
            key={i}
            className={`align-center min-w-100 ${isRanking ? 'item-ranking-table' : ''}`}
            style={{ height: 50 }}
          >
            {model.map((modelRow, _) => (
              <TableRow
                key={_}
                modelItem={modelRow}
                keyIndex={i}
                objectIndex={i}
              />
            ))}
          </Row>
        );
      }
    }

    return extraTableRender;
  };

  /**
   * Render table row item
   */
  const MemoRow = (propsMemoRow: any) =>
    useMemo(() => {
      const { trackingKeyProps, hasRowSpacing } = propsMemoRow;

      return (
        <Column
          className="relative"
          style={Object.assign(redirectUrl != null ? { display: 'flex' } : { display: 'grid' })}
          key={propsMemoRow.i}
        >
          {
            !IsNullOrEmpty(redirectUrl) && IsNullOrEmpty(sentMoney) ? (
              <Link to={transformUrl(redirectUrl!, propsMemoRow.item)}>
                <Row className="align-center min-w-100" style={{ height: rowHeight || 50 }}>
                  <Dropdown.Arrow index={propsMemoRow.i} />

                  {model.map((m, _) => (
                    <TableRow
                      key={_}
                      modelItem={m}
                      keyIndex={_}
                      objectIndex={propsMemoRow.i}
                    >
                      <TableItem
                        trackingKeyProps={trackingKeyProps}
                        item={propsMemoRow.item}
                        modelItem={m}
                        customProps={customProps}
                      />
                    </TableRow>
                  ))}
                </Row>
              </Link>
            ) : !IsNullOrEmpty(redirectUrl) && !IsNullOrEmpty(sentMoney) ? (
              <Row
                className={`align-center min-w-100 ${isRanking ? 'item-ranking-table' : ''}`}
                style={{ height: rowHeight || 50 }}
              >
                <Dropdown.Arrow index={propsMemoRow.i} />

                {model.map((m, _) => (
                  <Link key={_} to={transformUrl(redirectUrl!, propsMemoRow.item)}>
                    <TableRow
                      key={_}
                      modelItem={m}
                      keyIndex={_}
                      objectIndex={propsMemoRow.i}
                    >
                      <TableItem
                        trackingKeyProps={propsMemoRow.trackingKey}
                        item={propsMemoRow.item}
                        modelItem={m}
                        customProps={customProps}
                      />
                    </TableRow>
                  </Link>
                ))}
              </Row>
            ) : (
              <Row
                className={`align-center min-w-100 ${isRanking ? 'item-ranking-table' : ''}`}
                style={{ height: rowHeight || 50 }}
              >
                <Dropdown.Arrow index={propsMemoRow.i} />

                {model.map((m, _) => (
                  <TableRow
                    key={_}
                    modelItem={m}
                    keyIndex={_}
                    objectIndex={propsMemoRow.i}
                  >
                    <TableItem
                      trackingKeyProps={propsMemoRow.trackingKey}
                      item={propsMemoRow.item}
                      modelItem={m}
                      customProps={customProps}
                    />
                  </TableRow>
                ))}
              </Row>
            )
          }

          {portalCustomRow && hasRowSpacing && (
            <RowSpacing height={8} backgroundColor="transparent" />
          )}

          {expandedDropdown === propsMemoRow.i && DropdownTemplate && (
            <DropdownTemplate {...{ ...propsMemoRow.item, ...customProps }} />
          )}
        </Column>
      );
    }, [propsMemoRow]);

  const trackingAction = async (button: any) => {
    trackMenuClick({
      item: button.linkTo === '/protocolos' ?
        protocolosAction.LINK_DASHBOARD_VERTODOSPROTOCOLOS :
        pedidosAction.LINK_DASHBOARD_VERTODOSPEDIDOS
    });

    navigate(button.linkTo);
  };

  const handleCloseDownloadInLot = () => {
    setShowOptionsDownloadInLot(false);
  };

  const warningDownloadInLot = () => {
    if (selectedOrderDownloadInLot.length === 0)
      return 'Nenhum pedido selecionado';

    return selectedOrderDownloadInLot.length === 1 ?
      ' pedido selecionado' : ' pedidos selecionados';
  };

  return (
    <>
      <Column
        className={`relative flex-1 overflow-x-hidden ${className}` || ''}
        style={{
          minHeight:
            typeof data === 'undefined'
              ? 50 * (pageSize || 10) + 50 + (valueMinHeightBottomBtn)
              : 'auto',
          borderRadius: '10px 10px 0 0',
        }}
      >
        <Visibility visible={typeof data === 'undefined'}>
          <>
            <Visibility visible={!error}>
              <Loading size={45} />
            </Visibility>

            <Visibility visible={!!error}>
              <RefreshButton onReload={onReload} />
            </Visibility>
          </>
        </Visibility>

        <Column className="relative flex-1 overflow-x-auto">
          <Row
            className={`align-center ${isRanking ? 'label-title-ranking' : ''}`}
            style={{
              minHeight: labelHeight,
              height: labelHeight,
              maxHeight: labelHeight,
            }}
          >

            <Dropdown.Space />

            {TableLabel}
          </Row>

          {
            (typeof data === 'undefined' || data.length === 0) ? (
              <Row
                className="align-center justify-center"
                style={{ height: '50px', gridColumn: `span ${model.length}` }}
              >
                <Text fontStyle='italic'>{emptyDataMessage}</Text>
              </Row>
            ) : (
              data && data?.map((item, i) =>
                i < (pageSize || i + 1) && (
                  <MemoRow
                    key={i}
                    trackingKeyProps={props.trackingKey}
                    item={item}
                    i={i}
                    hasRowSpacing={portalCustomRow}
                  />
                )
              )
            )
          }

          <TableRowExtra />
          {hideShowTooltip && <Tooltip variant="light" />}
        </Column>

        <Visibility visible={!!showOptionsDownloadInLot}>
          <Row className="mt-2 bg-white d-flex justify-between selected-orders flex-1">
            <Text className="color-blue-gray" size={14}>
              {selectedOrderDownloadInLot.length > 0 && selectedOrderDownloadInLot.length}
              {warningDownloadInLot()}
            </Text>
            <Text className="color-blue-gray font-italic" size={14}>
              *Até {orderBulkDownload?.limit} pedidos podem ser selecionados
            </Text>
          </Row>
        </Visibility>

        {/**
         * Renders a "see more" button on table bottom
         */}
        {bottomButton && typeof data !== 'undefined' && (
          <Button onClick={() => trackingAction(bottomButton)} className="mt-2 w-100 px-0">
            <Row
              className="bg-gray-20 py-2 d-flex justify-center align-items w-100"
              style={{
                borderRadius: '0 0 10px 10px',
                height: 40,
                maxHeight: 40,
              }}
            >
              <Text
                size={12}
                className="d-flex align-center justify-center color-primary font-bold"
              >
                {bottomButton.label}
              </Text>
            </Row>
          </Button>
        )}
      </Column>

      <Row className={`align-center ajust-footer-elements 
         ${showOptionsDownloadInLot && downloadInLotButton ? "lote-employee" : "customer"}
         ${exportButton?.show || downloadInLotButton ? 'justify-between' : 'justify-center'}`}>

        {/* Renders a export button component if it's needed*/}
        {data !== undefined && data.length > 0 && downloadInLotButton ?
          <Column className={`pt-2 buttons-download ${classBorderDownloadInLote}`}>
            <ActionButton
              img={iconDownloadInLot}
              textButton="Download em Lote"
              action={() => downloadLoteInfo()}
            />

            {showOptionsDownloadInLot &&
              <OptionsDownloadInLot
                handleCloseDownloadInLot={handleCloseDownloadInLot}
                titleRequestDownload={orderBulkDownload?.titleRequestDownload}
                messageRequestDownload={orderBulkDownload?.messageRequestDownload}
                selectedOrderDownloadInLot={selectedOrderDownloadInLot}
              />}
          </Column>
          : null
        }

        {/* Renders a export button component if it's needed*/}
        {data !== undefined && data.length > 0 && exportButton?.show ?
          <Column className="pt-2 d-none export-button-responsive">
            <ActionButton
              img={iconDownload}
              textButton="Exportar Dados"
              action={exportButton.onClick}
            />
          </Column>
          : null}

        {/* Renders a pagination component if it's needed*/}
        <Column>
          {pageSize &&
            !bottomButton &&
            typeof data !== 'undefined' &&
            totalPages &&
            (showPagination == null || showPagination === true) &&
            totalPages > 1 ? (
            <Paginator
              pageSize={pageSize}
              currentPage={currentPage}
              totalPage={totalPages}
              onPageChange={onPageChange}
            />
          ) : null}
        </Column>

        {/* Renders a export button component if it's needed*/}
        {data !== undefined && data.length > 0 && exportButton?.show ?
          <Column className={`pt-2 hide-button ${showOptionsDownloadInLot && 'disable-btn-export-data'}`}>
            <ActionButton
              img={iconDownload}
              textButton="Exportar Dados"
              action={!showOptionsDownloadInLot && exportButton.onClick}
            />
          </Column>
          : null}
      </Row>
    </>
  );
};

export default Table;
